import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../CompositeComponents/Modal/Modal';
import { NumPad } from '../NumPad/NumPad';
import { PaymentTerminalStatus } from '../PaymentTerminalStatus/PaymentTerminalStatus';
import { DenominationEntry } from '../DenominationEntry/DenominationEntry';
import { Button } from '../../BasicComponents/Button/Button';
import { LoyaltyRedemptionInput } from '../../SharedDomainComponents/LoyaltyRedemptionInput/LoyaltyRedemptionInput';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { formatMoney } from '../../../utils/currency';
import type { CreditNoteLookupResult, PaymentMethod, PaymentModalProps, SplitPayment } from './types';
import type { DenominationRow } from '../DenominationEntry/types';
import {
  isRedeemable,
  maxRedeemablePoints,
  redemptionAmount,
  redemptionCoversBill,
} from './loyalty-redemption';
import {
  SPLIT_TENDER_METHODS,
  allocatedTotal,
  isSplitValid,
  splitTargetAmount,
  remainingToAllocate,
  rupeeStringToPaise,
  splitBlockReason,
  suggestNextAmount,
} from './split-payment';

const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash:         'Cash',
  upi:          'UPI',
  card:         'Card',
  wallet:       'Wallet',
  store_credit: 'Store Credit',
  credit_note:  'Credit Note',
  account_credit: 'Account Credit',
  split:        'Split',
};

const METHOD_ICONS: Record<PaymentMethod, string> = {
  cash:         '💵',
  upi:          '📱',
  card:         '💳',
  wallet:       '👛',
  store_credit: '🎟️',
  credit_note:  '🎫',
  account_credit: '📒',
  split:        '⚡',
};

// ─── Account Credit panel (ops simulation T08) ───────────────────────────────

function AccountCreditPanel({
  account,
  amount,
  fmt,
  onSplit,
  disabled,
}: {
  account: { creditLimit: number; outstanding: number };
  amount: number;
  fmt: (minor: number) => string;
  onSplit: () => void;
  disabled: boolean;
}) {
  const hasLimit = account.creditLimit > 0;
  const available = account.creditLimit - account.outstanding;
  const over = hasLimit ? account.outstanding + amount - account.creditLimit : 0;
  return (
    <div
      className={[
        'rounded-lg border px-4 py-3 text-sm',
        over > 0
          ? 'border-warning-300 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200'
          : 'border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200',
      ].join(' ')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span>Credit limit</span>
        <span className="font-mono tabular-nums">{hasLimit ? fmt(account.creditLimit) : 'No limit'}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-1">
        <span>Outstanding</span>
        <span className="font-mono tabular-nums">{fmt(account.outstanding)}</span>
      </div>
      {hasLimit && (
        <div className="flex items-baseline justify-between gap-3 mt-1">
          <span>Available</span>
          <span className="font-mono tabular-nums">{fmt(Math.max(0, available))}</span>
        </div>
      )}
      <div className="flex items-baseline justify-between gap-3 mt-1 font-semibold">
        <span>Put on account</span>
        <span className="font-mono tabular-nums">{fmt(amount)}</span>
      </div>
      {over > 0 && (
        <p role="status" className="mt-2 text-xs">
          This is {fmt(over)} over the credit limit. A manager must approve it with their PIN.
        </p>
      )}
      <Button variant="secondary" size="sm" className="mt-2" onClick={onSplit} disabled={disabled}>
        Pay part another way
      </Button>
    </div>
  );
}

// ─── PaymentModal ─────────────────────────────────────────────────────────────

export function PaymentModal({
  isOpen,
  onClose,
  totalAmount,
  onConfirm,
  isProcessing = false,
  currency = 'INR',
  cashRoundOff = 0,
  storeCreditAvailable = 0,
  loyalty,
  walletBalance,
  customerLinked,
  accountCredit,
  onCreditNoteLookup,
  defaultMethod,
  className,
  confirmTriggerRef,
}: PaymentModalProps) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  // Minor units -> the bare major-unit string a money <input> holds (no symbol,
  // no grouping). Decimals come from the currency, so a 3-decimal tenant gets
  // "12.345" where a hardcoded toFixed(2) produced "1234.50".
  const toInputString = (minor: number): string =>
    (minor / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency));

  // The store-credit tile is gated on the attached customer actually having
  // redeemable balance. The credit-note tile is shown whenever a lookup
  // function is wired — bearer credits work for walk-in carts too.
  const storeCreditEligible = storeCreditAvailable > 0;
  const creditNoteEligible = Boolean(onCreditNoteLookup);
  // Wallet debits a customer's points, so a walk-in bill cannot use it.
  const walletEligible = customerLinked !== false;
  // Account Credit (T08) needs the customer's linked credit account.
  const accountCreditEligible = Boolean(accountCredit);

  // Terminal default tender, guarded against methods this sale doesn't offer.
  const seededMethod: PaymentMethod =
    !defaultMethod ||
    (defaultMethod === 'store_credit' && !storeCreditEligible) ||
    (defaultMethod === 'credit_note' && !creditNoteEligible) ||
    (defaultMethod === 'wallet' && !walletEligible) ||
    (defaultMethod === 'account_credit' && !accountCreditEligible)
      ? 'cash'
      : defaultMethod;

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(seededMethod);
  const [cashTendered, setCashTendered] = useState('');
  const [denominations, setDenominations] = useState<DenominationRow[]>([]);
  // Points the cashier has chosen to spend on this bill. Seeded at 0 rather
  // than "all of them": spending a balance is the cashier's call to make with
  // the customer, not a default to be undone.
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  // Exact-paise methods drop the cash round-off — paying ₹397.95 via UPI
  // or Credit Note should not require an extra ₹0.05. CASH is the only
  // method that keeps the round-up.
  const isExactPaiseMethod =
    selectedMethod === 'upi' ||
    selectedMethod === 'card' ||
    selectedMethod === 'wallet' ||
    selectedMethod === 'store_credit' ||
    selectedMethod === 'credit_note' ||
    selectedMethod === 'account_credit';
  const effectiveTotal = isExactPaiseMethod ? totalAmount - cashRoundOff : totalAmount;

  // ── Credit-note bearer redemption ─────────────────────────────────────
  // Sub-modal state lives here so closing/re-opening the parent resets the
  // code-entry flow cleanly.
  const [creditNoteInput, setCreditNoteInput] = useState('');
  const [creditNoteLookupError, setCreditNoteLookupError] = useState<string | null>(null);
  const [creditNoteLookingUp, setCreditNoteLookingUp] = useState(false);
  const [resolvedCreditNote, setResolvedCreditNote] = useState<CreditNoteLookupResult | null>(null);

  // Split tenders. Amounts are paise; `splitDraftAmount` is the rupee string
  // being typed for the next row.
  const [splits, setSplits] = useState<SplitPayment[]>([]);
  const [splitDraftMethod, setSplitDraftMethod] = useState<PaymentMethod>('cash');
  const [splitDraftAmount, setSplitDraftAmount] = useState('');
  const splitAmountRef = useRef<HTMLInputElement>(null);

  const methods: PaymentMethod[] = [
    'cash',
    'upi',
    'card',
    'wallet',
    ...(storeCreditEligible ? (['store_credit'] as const) : []),
    ...(creditNoteEligible ? (['credit_note'] as const) : []),
    ...(accountCreditEligible ? (['account_credit'] as const) : []),
    'split',
  ];

  // Parsed through the same currency-aware, drift-free path as the split
  // tenders: `parseFloat(x) * 100` both assumed 2 decimals and reintroduced the
  // float drift (397.95 * 100 = 39794.999...) that left `canConfirm` false on
  // an exact tender.
  const cashTenderedPaise = rupeeStringToPaise(cashTendered, currency) || 0;
  const change = selectedMethod === 'cash' ? cashTenderedPaise - totalAmount : 0;
  // For pure store-credit checkout we can only redeem up to the smaller of
  // the available balance and the bill total. Partial coverage requires the
  // cashier to switch to Split tender.
  const storeCreditCovers = storeCreditAvailable >= effectiveTotal;

  // Loyalty redemption arithmetic — see loyalty-redemption.ts. Kept out of the
  // component so it is testable without a DOM, like the split allocation.
  const maxPoints = maxRedeemablePoints(loyalty, effectiveTotal);
  const redeemAmount = redemptionAmount(loyalty, pointsToRedeem);
  const redeemCovers = redemptionCoversBill(loyalty, pointsToRedeem, effectiveTotal);
  const creditNoteCovers =
    resolvedCreditNote ? resolvedCreditNote.amount_available >= effectiveTotal : false;

  // Split allocation, all in paise. The cash round-off stands only once a
  // cash leg is in the split; otherwise the split settles the exact total.
  // The suggestion counts the tender being typed, so "cash for the rest"
  // offers the rounded balance.
  const splitAllocated = allocatedTotal(splits);
  const splitTarget = splitTargetAmount(splits, totalAmount, cashRoundOff);
  const splitDraftTarget = splitTargetAmount(splits, totalAmount, cashRoundOff, splitDraftMethod);
  const splitRemaining = remainingToAllocate(splits, splitTarget);
  const splitReady = isSplitValid(splits, splitTarget);
  const splitReason = splitBlockReason(splits, splitTarget);
  // An empty box means "the rest": the placeholder shows that balance and Add
  // commits it, so the common close-out takes a tap instead of retyping a
  // figure the modal already knows. Derived every render, never copied into
  // state, so removing a tender re-suggests the corrected balance.
  //
  // It does NOT stand in for the first tender: the whole bill on one method is
  // not a split, and committing it would zero the balance, hide this panel and
  // strand the cashier on "needs at least two payment methods". The opening
  // amount is always typed (or filled with Rest).
  // CU2-007: a credit-note leg is paid from a looked-up note — at most its
  // value, so an empty box means "what the note can cover of the rest". One
  // note per split (the server takes one).
  const draftIsNote = splitDraftMethod === 'credit_note';
  const noteCap = resolvedCreditNote?.amount_available ?? 0;
  const splitHasNote = splits.some((s) => s.method === 'credit_note');
  const splitSuggestedPaise = draftIsNote
    ? Math.min(noteCap, suggestNextAmount(splits, splitDraftTarget))
    : suggestNextAmount(splits, splitDraftTarget);
  const splitDraftPaise = splitDraftAmount.trim()
    ? rupeeStringToPaise(splitDraftAmount, currency)
    : splits.length > 0 || draftIsNote
      ? splitSuggestedPaise
      : NaN;
  const canAddSplitTender =
    Number.isFinite(splitDraftPaise) && splitDraftPaise > 0 && remainingToAllocate(splits, splitDraftTarget) > 0 &&
    (!draftIsNote || (resolvedCreditNote !== null && !splitHasNote && splitDraftPaise <= noteCap));

  function addSplitTender() {
    if (!canAddSplitTender) return;
    setSplits((prev) => [
      ...prev,
      {
        method: splitDraftMethod,
        amount: splitDraftPaise,
        ...(draftIsNote && resolvedCreditNote ? { creditNote: resolvedCreditNote } : {}),
      },
    ]);
    setSplitDraftAmount('');
    if (draftIsNote) setSplitDraftMethod('cash');
  }

  function removeSplitTender(index: number) {
    setSplits((prev) => prev.filter((_, i) => i !== index));
    // Drop a typed draft too: it was sized against the old balance, and
    // leaving it would show the pre-removal figure next to the new one.
    setSplitDraftAmount('');
  }

  /**
   * "Switch to Split" from a short-fall panel. The panel's whole point is
   * combining the partial balance with another tender, so carry that balance
   * across as the first tender instead of dropping the cashier into an empty
   * form to retype it.
   */
  function switchToSplit(seedAmount?: number, seedMethod: PaymentMethod = 'store_credit') {
    setSelectedMethod('split');
    if (seedAmount && seedAmount > 0 && splits.length === 0) {
      const seeded = Math.min(seedAmount, totalAmount);
      // A credit-note leg carries its looked-up note (CU2-007).
      const note = seedMethod === 'credit_note' && resolvedCreditNote ? { creditNote: resolvedCreditNote } : {};
      setSplits([{ method: seedMethod, amount: seeded, ...note }]);
      setSplitDraftMethod('cash');
      setSplitDraftAmount(toInputString(totalAmount - seeded));
    }
  }

  /** Pre-fill the draft with whatever is still outstanding. */
  function fillRemaining() {
    if (splitSuggestedPaise <= 0) return;
    setSplitDraftAmount(toInputString(splitSuggestedPaise));
  }

  /**
   * Picking a tender focuses the amount box so the cashier can tap a method
   * and type straight away, without a second click into the field.
   *
   * The outstanding balance is NOT written into state here: it is shown as a
   * live placeholder (and applied by Add/Enter when the box is left empty), so
   * it always tracks the current tenders. Copying it into `splitDraftAmount`
   * would leave a stale figure behind the moment a tender is removed.
   */
  function selectSplitDraftMethod(method: PaymentMethod) {
    // Account Credit (T08): one tap puts whatever is still owed on account,
    // once another tender has started the split.
    if (method === 'account_credit' && splits.length > 0) {
      const rest = suggestNextAmount(splits, splitTargetAmount(splits, totalAmount, cashRoundOff, method));
      if (rest > 0) {
        setSplits((prev) => [...prev, { method, amount: rest }]);
        setSplitDraftAmount('');
        return;
      }
    }
    setSplitDraftMethod(method);
    splitAmountRef.current?.focus();
  }

  async function handleCreditNoteLookup() {
    if (!onCreditNoteLookup) return;
    // A scanned note QR is its verify link ending in `#CN-XXXXXX` (CU2-007);
    // the code is what follows the '#'.
    const typed = creditNoteInput.trim().toUpperCase();
    const code = /#(CN-[A-Z0-9]{6})$/.exec(typed)?.[1] ?? typed;
    if (!/^CN-[A-Z0-9]{6}$/.test(code)) {
      setCreditNoteLookupError('Code must be in the form CN-XXXXXX');
      return;
    }
    setCreditNoteLookupError(null);
    setCreditNoteLookingUp(true);
    try {
      const result = await onCreditNoteLookup(code);
      setResolvedCreditNote(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Lookup failed';
      setCreditNoteLookupError(message);
      setResolvedCreditNote(null);
    } finally {
      setCreditNoteLookingUp(false);
    }
  }

  function handleConfirm() {
    onConfirm(
      selectedMethod,
      selectedMethod === 'split' ? splits : undefined,
      selectedMethod === 'cash' ? denominations : undefined,
      selectedMethod === 'credit_note' ? (resolvedCreditNote ?? undefined) : undefined,
      selectedMethod === 'cash' ? cashTenderedPaise : undefined,
    );
  }

  const canConfirm =
    !isProcessing &&
    (selectedMethod !== 'cash' || cashTenderedPaise >= totalAmount) &&
    (selectedMethod !== 'store_credit' || storeCreditCovers) &&
    (selectedMethod !== 'credit_note' || (resolvedCreditNote !== null && creditNoteCovers)) &&
    // Blocked here rather than by a 400: the API requires ≥2 tenders summing
    // exactly to amount_paid.
    (selectedMethod !== 'split' || splitReady);

  // F10 integration: expose an imperative trigger the parent can call from
  // a keyboard handler. Assigned inline during render so the closure always
  // captures the latest state without needing an effect dep array. The
  // mount/unmount effect below clears the ref when the modal unmounts so
  // a stale closure can't outlive its component.
  if (confirmTriggerRef) {
    confirmTriggerRef.current = isOpen
      ? () => {
        if (!canConfirm) return false;
        handleConfirm();
        return true;
      }
      : null;
  }
  useEffect(() => {
    return () => {
      if (confirmTriggerRef) confirmTriggerRef.current = null;
    };
  }, [confirmTriggerRef]);

  // Clear the entry state whenever the modal opens. The modal is rendered
  // permanently with `isOpen` toggling visibility, so it never unmounts and
  // this state would otherwise survive into the next sale — the cashier would
  // see the previous customer's tendered amount pre-filled. Worse, `canConfirm`
  // only checks `tendered >= total`, so a stale over-tender stays "valid" and
  // the change due would be computed against money that was never handed over.
  useEffect(() => {
    if (isOpen) {
      setCashTendered('');
      setDenominations([]);
      setSelectedMethod(seededMethod);
      setCreditNoteInput('');
      setCreditNoteLookupError(null);
      setResolvedCreditNote(null);
      // Same hazard for split tenders: carrying them into the next sale would
      // settle a new bill with the previous customer's payment breakdown.
      setSplits([]);
      setSplitDraftMethod('cash');
      setSplitDraftAmount('');
    }
  }, [isOpen, seededMethod]);

  // Keep the caret in the split amount box so the cashier can just type: on
  // opening a split, after switching to Split, and after each tender is added
  // or removed. Without this the amount is the one field in the modal that
  // needs a click before it accepts digits.
  useEffect(() => {
    if (isOpen && selectedMethod === 'split' && splitRemaining > 0) {
      splitAmountRef.current?.focus();
    }
  }, [isOpen, selectedMethod, splits.length, splitRemaining]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Payment"
      variant="wide"
      closeOnBackdrop={false}
    >
      {/* Vertical rhythm is deliberately tight: with the numpad expanded the
          Confirm button fell below the fold, so the cashier had to scroll on
          every single sale. */}
      <div className={`space-y-3 ${className ?? ''}`}>
        {/* Total display */}
        <div className="text-center rounded-xl bg-brand-50 dark:bg-brand-900/20 py-2.5">
          <p className="text-[11px] font-medium text-brand-600 dark:text-brand-400 uppercase tracking-wide">
            Amount Due
          </p>
          <p className="text-3xl font-bold text-brand-700 dark:text-brand-300" aria-label={`Amount due ${fmt(effectiveTotal)}`}>
            {fmt(effectiveTotal)}
          </p>
          {isExactPaiseMethod && cashRoundOff !== 0 && (
            <p className="mt-1 text-[11px] text-brand-700/70 dark:text-brand-300/70">
              Cash round-off of {fmt(Math.abs(cashRoundOff))} dropped — exact paise charged.
            </p>
          )}
        </div>

        {/* Method selector */}
        <div role="radiogroup" aria-label="Payment method" className="flex gap-2 flex-wrap">
          {methods.map((method) => (
            <button
              key={method}
              type="button"
              role="radio"
              aria-checked={selectedMethod === method}
              onClick={() => setSelectedMethod(method)}
              disabled={isProcessing || (method === 'wallet' && !walletEligible)}
              title={method === 'wallet' && !walletEligible ? 'Link a customer to pay by wallet' : undefined}
              className={[
                'flex-1 min-w-[80px] flex flex-col items-center gap-0.5 py-2 px-2 rounded-xl border text-xs font-semibold transition-colors',
                selectedMethod === method
                  ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:border-brand-400 dark:text-brand-300'
                  : 'border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-500',
                isProcessing || (method === 'wallet' && !walletEligible)
                  ? 'opacity-50 cursor-not-allowed'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="text-base" aria-hidden="true">{METHOD_ICONS[method]}</span>
              {METHOD_LABELS[method]}
            </button>
          ))}
        </div>
        {!walletEligible && methods.includes('wallet') && (
          <p className="-mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            Wallet: link a customer to the bill to pay from their points.
          </p>
        )}

        {/* Cash: numpad + denominations */}
        {selectedMethod === 'cash' && (
          <div className="space-y-2">
            <NumPad
              value={cashTendered}
              onChange={setCashTendered}
              currency
              currencyCode={currency}
              allowDecimal
              label="Cash Tendered"
              compact
            />
            {cashTenderedPaise >= totalAmount && change > 0 && (
              <div className="flex justify-between text-sm font-semibold px-1">
                <span className="text-neutral-600 dark:text-neutral-400">Change</span>
                <span className="text-success-600 dark:text-success-400">{fmt(change)}</span>
              </div>
            )}
            {cashTenderedPaise > 0 && cashTenderedPaise < totalAmount && (
              <div
                role="status"
                className="flex justify-between text-sm font-semibold px-1 text-warning-700 dark:text-warning-400"
              >
                <span>Short by {fmt(totalAmount - cashTenderedPaise)}</span>
                <span className="font-normal text-xs self-center">
                  Tender at least {fmt(totalAmount)}
                </span>
              </div>
            )}
            <details>
              <summary className="text-xs text-brand-600 dark:text-brand-400 cursor-pointer select-none hover:underline">
                Denomination breakdown (optional)
              </summary>
              <div className="mt-2">
                <DenominationEntry
                  rows={denominations.length > 0 ? denominations : []}
                  onChange={setDenominations}
                  totalAmount={totalAmount}
                  currency={currency}
                />
              </div>
            </details>
          </div>
        )}

        {/* Card / UPI: terminal status. No terminal push is wired into checkout,
            so never claim "Connected": the cashier takes the payment on the
            device and Confirm only records it. */}
        {(selectedMethod === 'card' || selectedMethod === 'upi') && (
          <PaymentTerminalStatus
            status={isProcessing ? 'processing' : 'manual'}
          />
        )}

        {/* Store credit — show running balance + coverage hint. We don't
            need a numpad because the redemption amount is implicit: redeem
            up to the bill total. If the balance doesn't cover the bill the
            cashier is nudged to switch to Split. */}
        {selectedMethod === 'store_credit' && (
          <div
            className={[
              'rounded-lg border px-4 py-3 text-sm',
              storeCreditCovers
                ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20 text-success-700 dark:text-success-200'
                : 'border-warning-300 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200',
            ].join(' ')}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold">Store credit available</span>
              <span className="font-mono tabular-nums">{fmt(storeCreditAvailable)}</span>
            </div>
            {/* Pure store-credit tender is all-or-nothing, so a redemption
                figure is only honest when the balance covers the bill.
                Otherwise show the shortfall and a live route to Split. */}
            {storeCreditCovers ? (
              <div className="flex items-baseline justify-between gap-3 mt-1">
                <span>Will redeem</span>
                <span className="font-mono tabular-nums">{fmt(effectiveTotal)}</span>
              </div>
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <span>Short by</span>
                  <span className="font-mono tabular-nums">
                    {fmt(effectiveTotal - storeCreditAvailable)}
                  </span>
                </div>
                <p className="mt-2 text-xs">
                  Balance doesn&apos;t cover the bill. Switch to <strong>Split</strong> to
                  combine credit with another tender.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => switchToSplit(storeCreditAvailable)}
                  disabled={isProcessing}
                >
                  Switch to Split
                </Button>
              </>
            )}
          </div>
        )}

        {/* Credit note — bearer redemption sub-flow. Cashier types the
            CN-XXXXXX code from the customer's printed credit note. On
            successful lookup the panel switches to a confirmation summary;
            on confirm of the main modal the resolved code is
            forwarded to the bill-completion call. */}
        {selectedMethod === 'credit_note' && (
          <div className="space-y-3">
            {!resolvedCreditNote ? (
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 space-y-3">
                <label htmlFor="credit-note-code" className="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  Credit note code
                </label>
                <div className="flex gap-2">
                  <input
                    id="credit-note-code"
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder="CN-XXXXXX"
                    value={creditNoteInput}
                    onChange={(e) => setCreditNoteInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreditNoteLookup();
                      }
                    }}
                    disabled={creditNoteLookingUp || isProcessing}
                    className="flex-1 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-mono tabular-nums tracking-widest px-3 py-2 text-center text-lg uppercase focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <Button
                    variant="primary"
                    onClick={handleCreditNoteLookup}
                    disabled={creditNoteLookingUp || isProcessing || creditNoteInput.trim().length < 8}
                  >
                    {creditNoteLookingUp ? 'Looking up…' : 'Look up'}
                  </Button>
                </div>
                {creditNoteLookupError && (
                  <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                    {creditNoteLookupError}
                  </p>
                )}
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  The 6-character code is printed on the customer&apos;s credit-note receipt.
                </p>
              </div>
            ) : (
              <div
                className={[
                  'rounded-lg border px-4 py-3 text-sm',
                  creditNoteCovers
                    ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20 text-success-700 dark:text-success-200'
                    : 'border-warning-300 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200',
                ].join(' ')}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-semibold">
                    {resolvedCreditNote.code}
                  </span>
                  <button
                    type="button"
                    className="text-xs underline hover:no-underline"
                    onClick={() => {
                      setResolvedCreditNote(null);
                      setCreditNoteInput('');
                      setCreditNoteLookupError(null);
                    }}
                  >
                    Change code
                  </button>
                </div>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <span>Available</span>
                  <span className="font-mono tabular-nums">
                    {fmt(resolvedCreditNote.amount_available)}
                  </span>
                </div>
                {/* Same all-or-nothing rule as store credit: only advertise a
                    redemption the Confirm button can actually perform. */}
                {creditNoteCovers ? (
                  <>
                    <div className="flex items-baseline justify-between gap-3 mt-1">
                      <span>Will redeem</span>
                      <span className="font-mono tabular-nums">{fmt(effectiveTotal)}</span>
                    </div>
                    {resolvedCreditNote.amount_available > effectiveTotal && (
                      <div className="flex items-baseline justify-between gap-3 mt-1">
                        <span>Returned as a new credit note</span>
                        <span className="font-mono tabular-nums">
                          {fmt(resolvedCreditNote.amount_available - effectiveTotal)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between gap-3 mt-1">
                      <span>Short by</span>
                      <span className="font-mono tabular-nums">
                        {fmt(effectiveTotal - resolvedCreditNote.amount_available)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs">
                      Credit doesn&apos;t cover the bill. Use <strong>Split</strong>: the note pays its part and
                      another tender pays the rest.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => switchToSplit(resolvedCreditNote.amount_available, 'credit_note')}
                      disabled={isProcessing}
                    >
                      Switch to Split
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Account Credit (T08) — the whole amount goes on the customer's
            credit account. The limit is shown, not enforced: over it, the
            server asks for a manager's PIN. */}
        {selectedMethod === 'account_credit' && accountCredit && (
          <AccountCreditPanel
            account={accountCredit}
            amount={effectiveTotal}
            fmt={fmt}
            onSplit={() => switchToSplit()}
            disabled={isProcessing}
          />
        )}

        {/* Split tender entry. The API needs at least two tenders summing
            exactly to the bill, so the running allocation is shown and Confirm
            stays disabled until it balances. */}
        {selectedMethod === 'split' && (
          <div className="space-y-3">
            {/* Tenders added so far */}
            {splits.length > 0 && (
              <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-600">
                {splits.map((s, i) => (
                  <li key={`${s.method}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                      <span aria-hidden="true">{METHOD_ICONS[s.method]}</span>
                      {METHOD_LABELS[s.method]}
                      {s.creditNote && <span className="font-mono text-xs">{s.creditNote.code}</span>}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono text-sm tabular-nums text-neutral-900 dark:text-neutral-100">
                        {fmt(s.amount)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSplitTender(i)}
                        disabled={isProcessing}
                        aria-label={`Remove ${METHOD_LABELS[s.method]} tender of ${fmt(s.amount)}`}
                        className="text-xs font-medium text-danger-600 hover:underline disabled:opacity-50 dark:text-danger-400"
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Next tender */}
            {splitRemaining > 0 && (
              <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-600">
                <div className="flex flex-wrap gap-2">
                  {SPLIT_TENDER_METHODS.filter(
                    (m) =>
                      (m !== 'store_credit' || storeCreditEligible) &&
                      (m !== 'credit_note' || (creditNoteEligible && !splitHasNote)) &&
                      (m !== 'account_credit' || accountCreditEligible),
                  ).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => selectSplitDraftMethod(m)}
                      disabled={isProcessing}
                      aria-pressed={splitDraftMethod === m}
                      className={[
                        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                        splitDraftMethod === m
                          ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                          : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700',
                      ].join(' ')}
                    >
                      <span aria-hidden="true" className="mr-1">{METHOD_ICONS[m]}</span>
                      {METHOD_LABELS[m]}
                    </button>
                  ))}
                </div>

                {draftIsNote && (
                  <div className="mt-3 space-y-2">
                    {!resolvedCreditNote ? (
                      <>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            autoCapitalize="characters"
                            autoComplete="off"
                            placeholder="CN-XXXXXX"
                            aria-label="Split credit note code"
                            value={creditNoteInput}
                            onChange={(e) => setCreditNoteInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreditNoteLookup();
                              }
                            }}
                            disabled={creditNoteLookingUp || isProcessing}
                            className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-center font-mono text-sm uppercase tracking-widest focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleCreditNoteLookup}
                            disabled={creditNoteLookingUp || isProcessing || creditNoteInput.trim().length < 8}
                          >
                            {creditNoteLookingUp ? 'Looking up…' : 'Look up'}
                          </Button>
                        </div>
                        {creditNoteLookupError && (
                          <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {creditNoteLookupError}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="flex items-baseline justify-between gap-3 rounded-md bg-violet-50 px-3 py-2 text-sm text-violet-800 dark:bg-violet-900/20 dark:text-violet-200">
                        <span className="font-semibold">{resolvedCreditNote.code}</span>
                        <span>
                          Available <span className="font-mono tabular-nums">{fmt(noteCap)}</span>
                        </span>
                        <button
                          type="button"
                          className="text-xs underline hover:no-underline"
                          onClick={() => {
                            setResolvedCreditNote(null);
                            setCreditNoteInput('');
                            setCreditNoteLookupError(null);
                          }}
                        >
                          Change code
                        </button>
                      </div>
                    )}
                    {resolvedCreditNote && splitDraftPaise > noteCap && (
                      <p role="status" className="text-xs text-warning-700 dark:text-warning-400">
                        The note has only {fmt(noteCap)}.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <input
                    ref={splitAmountRef}
                    type="text"
                    inputMode="decimal"
                    value={splitDraftAmount}
                    onChange={(e) => setSplitDraftAmount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canAddSplitTender) {
                        e.preventDefault();
                        addSplitTender();
                      }
                    }}
                    placeholder={
                      splits.length > 0 || draftIsNote ? toInputString(splitSuggestedPaise) : toInputString(0)
                    }
                    aria-label="Tender amount"
                    disabled={isProcessing}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-right font-mono text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                  />
                  <Button variant="secondary" size="sm" onClick={fillRemaining} disabled={isProcessing}>
                    Rest
                  </Button>
                  <Button variant="secondary" size="sm" onClick={addSplitTender} disabled={!canAddSplitTender || isProcessing}>
                    Add
                  </Button>
                </div>
              </div>
            )}

            {/* Running allocation */}
            <div className="rounded-lg bg-neutral-50 px-4 py-3 text-sm dark:bg-neutral-800">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-neutral-600 dark:text-neutral-400">Allocated</span>
                <span className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                  {fmt(splitAllocated)} / {fmt(splitTarget)}
                </span>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-neutral-600 dark:text-neutral-400">
                  {splitRemaining < 0 ? 'Over by' : 'Remaining'}
                </span>
                <span
                  className={[
                    'font-mono tabular-nums font-semibold',
                    splitRemaining === 0
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-warning-600 dark:text-warning-400',
                  ].join(' ')}
                >
                  {fmt(Math.abs(splitRemaining))}
                </span>
              </div>
              {splitReason && (
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{splitReason}</p>
              )}
            </div>
          </div>
        )}

        {/*
          Wallet.

          Without a loyalty context this is the original placeholder: the whole
          bill settles from the wallet and the server derives the points. With
          one, the cashier can choose how many points to spend — which is what
          FR-CRM-005 and FR-CRM-023 ask for and what the till has never been
          able to do.

          A partial redemption is expressed as a SPLIT with a WALLET leg. That
          path already exists and is already validated server-side
          (`assertWalletCovers` on the leg amount, which is also where the
          tenant's redemption floor is enforced), so nothing new is trusted to
          the client.
        */}
        {selectedMethod === 'wallet' && !loyalty && (
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800 px-4 py-3 text-sm text-neutral-600 dark:text-neutral-400 text-center space-y-1">
            {walletBalance && (
              <p className="font-semibold text-neutral-800 dark:text-neutral-100">
                Wallet balance: {fmt(walletBalance.valueMinor)} (
                {walletBalance.points.toLocaleString('en-IN')} points available)
              </p>
            )}
            {walletBalance && walletBalance.valueMinor < totalAmount ? (
              <p role="status" className="text-warning-700 dark:text-warning-400">
                Not enough to cover this bill — use Split to add another tender.
              </p>
            ) : (
              <p>Customer wallet will be debited on confirmation</p>
            )}
          </div>
        )}

        {selectedMethod === 'wallet' && loyalty && (
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-600 px-4 py-3">
            <LoyaltyRedemptionInput
              availablePoints={loyalty.availablePoints}
              pointValue={loyalty.pointValuePaise}
              currency={currency}
              maxRedeemable={maxPoints}
              value={pointsToRedeem}
              onChange={setPointsToRedeem}
              billTotal={effectiveTotal}
              minimumPoints={loyalty.minimumPoints}
              disabled={isProcessing}
            />
            {redeemCovers ? (
              <p className="mt-2 text-xs text-success-700 dark:text-success-300">
                These points cover the bill. Confirm to settle it from the wallet.
              </p>
            ) : (
              <>
                <div className="mt-2 flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">Still to pay</span>
                  <span className="font-mono tabular-nums">
                    {fmt(effectiveTotal - redeemAmount)}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => switchToSplit(redeemAmount, 'wallet')}
                  disabled={isProcessing || !isRedeemable(loyalty, pointsToRedeem)}
                >
                  Pay the rest another way
                </Button>
              </>
            )}
          </div>
        )}

        {/* Confirm */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleConfirm}
          disabled={!canConfirm}
          loading={isProcessing}
          aria-label="Confirm payment"
        >
          Confirm Payment
        </Button>
      </div>
    </Modal>
  );
}
